import React, { useEffect, useState, useRef, useCallback } from 'react';
import classnames from 'classnames';

// Types
import { WpDataTableProps, SortDirections } from './types';

// Components
import { WpContainer } from '../container';
import TableHeader from './TableHeader';
import TableRows from './TableRows';
import TableLoading from './TableLoading';
import TableBreadcrumbs from './TableBreadcrumbs';

// Styles
import './WpDataTable.scss';

const WpDataTableV2 = ({
  tableId,
  columns,
  data,
  striped = false,
  rowClick,
  showHeader = true,
  showInContainer = true,
  isLoading = false,
  slimRow = false,
  showBorder = true,
  nestedKey,
  uniqueIdentifier = () => null,
  labelIdentifier = () => null,
  singleItemLabel = 'Item',
  pluralItemLabel = 'Items',
  windowedScrolling = false,
  maxHeight,
  selectedItems,
  selectableSelector,
  onSelect,
}: WpDataTableProps) => {
  const [sortedColumn, setSortColumn] = useState(null);
  const [sortedDirection, setSortDirection] = useState(null);
  const [sortedData, setSortedData] = useState([]);
  const [selectedSubItem, setSelectedSubItem] = useState(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const hasNesting = data && nestedKey ? Boolean(data.find(d => nestedKey(d))) : false;

  // TODO: (josh) - enable multi select on nested tables
  const canMultiSelect = onSelect && selectableSelector && !hasNesting;

  const ref = useRef<HTMLDivElement>(null);

  const handleSelectSubItem = item => {
    setIsSwitching(true);
    // !item ? setSelectedSubItem([item]) : setSelectedSubItem(item);
    setSelectedSubItem(item);
    setTimeout(() => setIsSwitching(false), 300);
  };

  const handleSelectRowsChange = newKeys => {
    setSelectedRowKeys(newKeys);
    if (onSelect) onSelect(data.filter(item => newKeys.includes(selectableSelector(item))));
  };

  const handleSelectRow = useCallback(
    item => {
      if (!selectableSelector) return;
      const key = selectableSelector(item);
      handleSelectRowsChange([...selectedRowKeys, key]);
    },
    [selectedRowKeys]
  );

  const handleUnSelectRow = useCallback(
    item => {
      if (!selectableSelector) return;
      const key = selectableSelector(item);
      handleSelectRowsChange(selectedRowKeys.filter(sk => sk !== key));
    },
    [selectedRowKeys]
  );

  const handleSelectAllChange = useCallback(() => {
    if (!selectableSelector) return;

    if (selectedRowKeys.length === data.length) {
      handleSelectRowsChange([]);
    } else {
      handleSelectRowsChange(data.map(item => selectableSelector(item)));
    }
  }, [selectedRowKeys]);

  useEffect(() => {
    const defaultSortedColumn = columns.find(column => column.sort);

    if (defaultSortedColumn) {
      setSortColumn(defaultSortedColumn.label);
      setSortDirection(defaultSortedColumn.sort);
    }
  }, []);

  useEffect(() => {
    if (selectedItems && selectableSelector && canMultiSelect) {
      if (selectedItems.length === 0) {
        setSelectedRowKeys([]);
      } else {
        setSelectedRowKeys(selectedItems.map(item => selectableSelector(item)));
      }
      // handleSelectRowsChange(defaultSelected.map(item => selectableSelector(item)));
    }
  }, [selectedItems]);

  useEffect(() => {
    const selector = columns.find(column => column.label === sortedColumn)?.selector;
    // ensure we have a sorted column, direction, valid selector for column
    if (sortedColumn && sortedDirection && selector) {
      const newSortedRows = [...data].sort((a, b) => {
        const selector = columns.find(column => column.label === sortedColumn).selector;
        const [first, second] = sortedDirection == SortDirections.ASC ? [a, b] : [b, a];

        const firstValue = selector(first)?.toLocaleLowerCase();
        const secondValue = selector(second)?.toLocaleLowerCase();

        if (firstValue < secondValue) {
          return -1;
        } else if (firstValue > secondValue) {
          return 1;
        } else {
          return 0;
        }
      });

      setSortedData(newSortedRows);
    } else {
      setSortedData(data);
    }
  }, [data, sortedColumn, sortedDirection]);

  const baseClassName = 'WpDataTableV2';

  const tableMarkup = (
    <table id={tableId}>
      {showHeader && (
        <TableHeader
          tableId={tableId}
          columns={columns}
          sortedColumn={sortedColumn}
          sortedDirection={sortedDirection}
          setSortColumn={setSortColumn}
          setSortDirection={setSortDirection}
          showBorder={showBorder}
          hasNesting={hasNesting}
          selectable={canMultiSelect}
          handleSelectAllChange={handleSelectAllChange}
          data={data}
          selectedRowKeys={selectedRowKeys}
        />
      )}
      {isLoading || isSwitching ? (
        <TableLoading tableId={tableId} showBorder={showBorder} countToRender={selectedSubItem ? 3 : 6} />
      ) : (
        <>
          <TableBreadcrumbs
            tableId={tableId}
            data={data}
            selectedData={selectedSubItem}
            nestedKey={nestedKey}
            uniqueIdentifier={uniqueIdentifier}
            handleSelectSubItem={handleSelectSubItem}
            labelIdentifier={labelIdentifier}
            pluralItemLabel={pluralItemLabel}
          />
          <TableRows
            tableId={tableId}
            columns={columns}
            data={selectedSubItem ? selectedSubItem : sortedData}
            striped={striped}
            onClick={rowClick}
            slimRow={slimRow}
            showBorder={showBorder}
            nestedKey={nestedKey}
            hasNesting={hasNesting}
            handleSelectSubItem={handleSelectSubItem}
            expandAll={selectedSubItem ? true : false}
            pluralItemLabel={pluralItemLabel}
            singleItemLabel={singleItemLabel}
            viewportRef={ref}
            enableWindowScrolling={windowedScrolling}
            selectable={canMultiSelect}
            selectableSelector={selectableSelector}
            selectedRowKeys={selectedRowKeys}
            handleSelectRow={handleSelectRow}
            handleUnSelectRow={handleUnSelectRow}
          />
        </>
      )}
    </table>
  );

  const containerClasses = classnames(baseClassName, windowedScrolling && `${baseClassName}--windowed-scrolling`);
  const classes = classnames(
    baseClassName,
    windowedScrolling && `${baseClassName}--windowed-scrolling`,
    showBorder && `${baseClassName}__top-border`
  );

  return (
    <>
      {showInContainer ? (
        <WpContainer className={containerClasses} containerRef={ref} styles={{ maxHeight }}>
          {tableMarkup}
        </WpContainer>
      ) : (
        <div className={classes} ref={ref} style={{ maxHeight }}>
          {tableMarkup}
        </div>
      )}
    </>
  );
};

export default WpDataTableV2;
